import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { categories, featuredArticles, newArticles } from '@/mocks/categories';

export default function Home() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hash]);

  const allArticles = [...featuredArticles, ...newArticles];

  return (
    <>
      <SEOHead
        title="The Complete LitBuy Spreadsheet Resource Center"
        description="Discover expert guides, free templates, honest comparisons, and productivity tips — everything you need to master LitBuy Spreadsheet and boost your workflow."
        canonicalPath="/"
        pageType="website"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20modern%20workspace%20with%20laptop%20on%20a%20light%20wooden%20desk%20surrounded%20by%20neatly%20arranged%20documents%20and%20a%20small%20potted%20plant%20soft%20natural%20daylight%20streaming%20through%20a%20window%20warm%20cream%20and%20beige%20tones%20airy%20and%20serene%20atmosphere%20editorial%20photography%20with%20gentle%20shadows%20and%20harmonious%20composition&width=1800&height=900&seq=hero-main-bg-v5&orientation=landscape"
            alt="Modern workspace with LitBuy Spreadsheet"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
        </div>
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
          <p className="text-xs md:text-sm font-semibold text-white/80 uppercase tracking-[0.25em] mb-5">
            Your Complete Resource Hub
          </p>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl mx-auto">
            The Complete LitBuy Spreadsheet Resource Center
          </h1>
          <p className="mt-5 text-sm md:text-base text-white/80 max-w-xl mx-auto leading-relaxed">
            Discover expert guides, free templates, honest comparisons, and productivity tips — everything you need to master LitBuy Spreadsheet and boost your workflow.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-primary-500 text-white rounded-full text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              litbuy spreadsheet
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-white/90 text-foreground-900 rounded-full text-sm md:text-base font-semibold hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              buy it now
              <i className="ri-shopping-bag-line text-lg"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Product Category Grid */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-primary-500 uppercase tracking-widest mb-3">Browse Categories</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              Explore Our Product Collections
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
              Discover premium products across all categories at kaspreadsheet.com
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group relative rounded-xl overflow-hidden aspect-[3/4] bg-background-100 cursor-pointer"
              >
                <img
                  src={cat.image}
                  alt={`${cat.name} collection`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white font-semibold text-sm md:text-base whitespace-nowrap">{cat.name}</h3>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Category Image Cards */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              Quick Category Links
            </h2>
            <p className="mt-2 text-foreground-600 text-sm">Jump directly to any product category — each with a visual preview</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group flex flex-col items-center gap-2.5 p-3 rounded-xl hover:bg-background-50 transition-colors cursor-pointer"
              >
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden bg-background-200 flex-shrink-0">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
                <span className="text-xs md:text-sm font-medium text-foreground-800 text-center leading-tight whitespace-nowrap group-hover:text-primary-500 transition-colors">
                  {cat.name}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Feature Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-accent-500 uppercase tracking-widest mb-3">Why Choose LitBuy</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              Everything You Need in One Place
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
              LitBuy Spreadsheet combines powerful features with an intuitive design to help you work smarter.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: 'ri-guide-line',
                title: 'Comprehensive Guides',
                desc: 'Step-by-step tutorials and in-depth articles designed for beginners and advanced users alike. Learn everything from basic setup to advanced workflows.',
              },
              {
                icon: 'ri-file-list-3-line',
                title: 'Free Templates',
                desc: 'Access a growing library of professionally designed templates for inventory, orders, tracking, and more. Save hours of setup time.',
              },
              {
                icon: 'ri-scales-3-line',
                title: 'Honest Comparisons',
                desc: 'Unbiased side-by-side comparisons with Google Sheets, Excel, Airtable, and other popular tools. Make informed decisions.',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Safe & Secure',
                desc: 'Your data security matters. Learn about built-in protection features and best practices for keeping your spreadsheets secure.',
              },
              {
                icon: 'ri-rocket-line',
                title: 'Productivity Boost',
                desc: 'Proven tips and techniques to streamline your workflow, automate repetitive tasks, and get more done in less time.',
              },
              {
                icon: 'ri-question-answer-line',
                title: 'Expert FAQ Hub',
                desc: 'Quick answers to the most common questions about LitBuy Spreadsheet. Find solutions fast without digging through documentation.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-background-100 rounded-xl p-6 md:p-7">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mb-4">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles Grid */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-primary-500 uppercase tracking-widest mb-3">Learn &amp; Grow</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              Featured Guides &amp; Articles
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
              Dive deep into our most popular resources and become a LitBuy Spreadsheet expert.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {featuredArticles.map((article, idx) => (
              <Link
                key={article.path}
                to={article.path}
                className="group bg-white rounded-xl overflow-hidden hover:shadow-sm transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden bg-secondary-100/50 relative">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 md:p-6">
                  {idx === 0 && (
                    <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-semibold mb-3 whitespace-nowrap">
                      Pillar Guide
                    </span>
                  )}
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.description}</p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-medium text-primary-500">
                    Read Article
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* New SEO Articles - 9 Articles */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-accent-500 uppercase tracking-widest mb-3">In-Depth Resources</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              More LitBuy Spreadsheet Insights
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
              Explore our expanded library of detailed guides covering every aspect of LitBuy Spreadsheet.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {newArticles.map((article) => (
              <Link
                key={article.path}
                to={article.path}
                className="group bg-background-100 rounded-xl overflow-hidden hover:shadow-sm transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden bg-background-200 relative">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.description}</p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-medium text-accent-500">
                    Read Article
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            <div className="w-full lg:w-1/2">
              <div className="rounded-[2rem] overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20workspace%20with%20two%20monitors%20displaying%20colorful%20spreadsheet%20dashboards%20and%20analytics%20warm%20ambient%20lighting%20from%20a%20desk%20lamp%20clean%20minimalist%20desk%20setup%20with%20a%20notebook%20and%20coffee%20cup%20cream%20and%20soft%20beige%20color%20palette%20editorial%20photography%20with%20cozy%20productive%20atmosphere&width=900&height=700&seq=about-litbuy-v5&orientation=landscape"
                  alt="LitBuy Spreadsheet workspace"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <span className="inline-block px-3 py-1.5 bg-accent-100 text-accent-700 rounded-full text-xs font-semibold mb-4 whitespace-nowrap">
                About This Resource
              </span>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                Your Trusted LitBuy Spreadsheet Knowledge Base
              </h2>
              <p className="mt-5 text-foreground-600 text-sm md:text-base leading-relaxed">
                We built LitBuy Spreadsheet Resource Center to be the most comprehensive, easy-to-navigate source of information for anyone using or considering LitBuy Spreadsheet. Whether you are a complete beginner looking for your first template or an experienced user seeking advanced workflow tips, our guides are crafted with clarity and depth in mind.
              </p>
              <p className="mt-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                Every article is thoroughly researched, regularly updated, and designed to give you actionable insights — not just theory. We believe in honest comparisons, practical templates, and transparent information that helps you make the best decisions for your productivity.
              </p>
              <div className="mt-6">
                <Link
                  to="/litbuy-spreadsheet"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Read the Ultimate Guide
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            Ready to Explore LitBuy Spreadsheet?
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
            Visit the main website to browse products, access templates, and start your productivity journey today.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-primary-500 text-white rounded-full text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              litbuy spreadsheet
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-white border-2 border-foreground-200 text-foreground-900 rounded-full text-sm md:text-base font-semibold hover:border-primary-500 hover:text-primary-500 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              buy it now
              <i className="ri-shopping-bag-line text-lg"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}